import React from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, Tag, Percent, Calculator, Users, Building2, SlidersHorizontal, BookOpen } from 'lucide-react';
import { ListSection, ListRow, PrimaryButton } from '../../components/RowList';
import SubmitBudgetButton from '../../components/SubmitBudgetButton';

// Module 6 — Revenue & Commercial "Implosion" (the "Profit" module).
const Revenue = () => {
  const navigate = useNavigate();

  return (
    <div className="h-full overflow-y-auto font-sans bg-slate-100 dark:bg-slate-900">
      <div className="max-w-6xl mx-auto px-6 py-10">

        <div className="mb-10 flex items-start justify-between gap-4">
          <div>
            <h1 className="m-0 text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">Revenue</h1>
            <p className="mt-2 text-base text-slate-500 dark:text-slate-400">
              The strategic engine — aggregate costs from every module into a bottom-up break-even and set prices with confidence.
            </p>
          </div>
          <SubmitBudgetButton module="MOD_6_PROFIT" />
        </div>

        <ListSection title="Volumes">
          <ListRow
            icon={TrendingUp}
            title="Sales / Sell-out Volumes"
            description="Enter or import sell-out and customer sales volumes by SKU and month — each row targets a site, a customer, or a customer group. Edit inline in the budget grid, or round-trip an Excel template (append / overwrite / replace)."
            actions={<PrimaryButton onClick={() => navigate('/revenue/sales-volumes')}>Open</PrimaryButton>}
          />
        </ListSection>

        {/* Customer master data — populates md_customers / md_customer_groups,
            the reference lists the sales-volume screen plans against. Same
            grid + Excel round-trip mechanism as the volume screens. */}
        <ListSection title="Customer Information">
          <ListRow
            icon={Building2}
            title="Customer Groups"
            description="Maintain channel / key-account groupings (e.g. Major Retail, Wholesale). Add inline, or round-trip an Excel template. Load these before customers, which reference their group."
            actions={<PrimaryButton onClick={() => navigate('/revenue/customer-groups')}>Open</PrimaryButton>}
          />
          <ListRow
            icon={Users}
            title="Customers"
            description="Maintain the customer master — code, name and the group each customer belongs to. These become the customer / group dimensions on the sales-volume screen."
            actions={<PrimaryButton onClick={() => navigate('/revenue/customers')}>Open</PrimaryButton>}
          />
        </ListSection>

        <ListSection title="Pricing & Margin">
          <ListRow
            icon={Tag}
            title="Selling Prices (per FG)"
            description="List price per finished good per period — phase price increases into the month they land."
            actions={<PrimaryButton onClick={() => navigate('/revenue/prices')}>Open</PrimaryButton>}
          />
          <ListRow
            icon={Percent}
            title="Discounts & Trade Spend"
            description="Trade promotions, rebates and settlement discounts per customer or group, by month — the bridge from gross to net revenue."
            actions={<PrimaryButton onClick={() => navigate('/revenue/discounts')}>Open</PrimaryButton>}
          />
          <ListRow
            icon={Calculator}
            title="Price Implosion"
            description="Roll every cost — materials, manufacturing, distribution, overhead recovery — into a break-even price and target list price per SKU."
            actions={<PrimaryButton onClick={() => navigate('/revenue/implosion')}>Open</PrimaryButton>}
          />
        </ListSection>

        {/* Catalogue, not capture — the same split as the two distribution
            posting pages. Deciding that sales is GL 4000 has one owner. */}
        <ListSection title="Budget Setup">
          <ListRow
            icon={BookOpen}
            title="Revenue Posting"
            description="Where gross revenue and discounts land in the ledger. Map it once for all customers, then add exceptions by customer or customer group — and by discount type where a particular kind of spend posts elsewhere. Without this, revenue is absent from the consolidated P&L."
            actions={<PrimaryButton onClick={() => navigate('/revenue/budget-setup/posting')}>Open</PrimaryButton>}
          />
        </ListSection>

        <ListSection title="Configuration">
          <ListRow
            icon={SlidersHorizontal}
            title="Discount Configuration"
            description="Choose which discount types appear on the Discounts & Trade Spend screen — turn off the ones your business doesn't use to keep the grid focused."
            actions={<PrimaryButton onClick={() => navigate('/revenue/discount-config')}>Open</PrimaryButton>}
          />
        </ListSection>

      </div>
    </div>
  );
};

export default Revenue;
